import { NgTemplateOutlet } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { KENDO_BUTTON } from "@progress/kendo-angular-buttons";
import { KENDO_DATEINPUTS } from "@progress/kendo-angular-dateinputs";
import { KENDO_DROPDOWNLIST, KENDO_MULTISELECT } from "@progress/kendo-angular-dropdowns";
import { DataStateChangeEvent, GridDataResult, GridSmartBoxSearchEvent, GridSmartBoxSearchSettings, KENDO_GRID, RowArgs } from "@progress/kendo-angular-grid";
import { ExcelExportData } from "@progress/kendo-angular-excel-export";
import { NotificationService } from "@progress/kendo-angular-notification";
import { KENDO_TOOLBAR } from "@progress/kendo-angular-toolbar";
import { KENDO_SVGICON } from "@progress/kendo-angular-icons";
import { KENDO_POPUP } from "@progress/kendo-angular-popup";
import { CompositeFilterDescriptor, FilterDescriptor, State } from "@progress/kendo-data-query";
import { checkIcon, fileExcelIcon, filterClearIcon, pencilIcon, pinIcon, unpinIcon } from "@progress/kendo-svg-icons";
import { Observable, Subject, catchError, debounceTime, filter, map, merge, of, startWith, switchMap, tap } from "rxjs";
import { FlightDetailComponent } from "./flight-detail/flight-detail.component";
import { FlightEditDialogComponent } from "./flight-edit-dialog/flight-edit-dialog.component";
import { ToolbarSlotComponent } from "../shared/toolbar-slot.component";
import { EXPORT_LIMIT, Flights2Service } from "./flights2.service";
import { BootstrapService } from "../shared/bootstrap/bootstrap.service";
import { FieldValuePipe, FixedPipe, LogDatePipe, LogTimePipe, OriginalFlightPipe, ZonedDateTimePipe, fieldValueOf, logDateOf, logTimeOf, zonedDateTimeOf } from "./flights2.pipes";
import { Flight } from "./models/flight.model";
import { inTimeZone, originalOf } from "./flights2.time-zone";
import { offsetFromUtc, offsetText } from "../shared/time-zone-offset";
import { FlightSearch } from "./models/flight-search.model";
import { Hoist } from "./models/hoist.model";
import { Hook } from "./models/hook.model";
import { offsetFromBrowser } from "../shared/time-zone-offset";
import { COLUMN_GROUPS, EXPAND_COLUMN_WIDTH, PAGE_SIZES, PIN_COLUMN_WIDTH, SEARCHED_FIELDS, SEARCH_DELAY_MS, columnsOf, withoutSecondEngine } from "./flights2.constants";
import { AirportHint } from "./models/airport-hint.model";
import { FlightListColumn } from "./models/flight-list-column.model";

/** Whether the range runs forward: the getter refuses one that ends before it starts. */
function forwardRange(group: AbstractControl): ValidationErrors | null {
    const { from, to } = group.value;
    return from && to && from > to ? { range: true } : null;
}

@Component({
    selector: "app-flights2",
    imports: [
        NgTemplateOutlet,
        ReactiveFormsModule,
        TranslatePipe,
        KENDO_GRID,
        KENDO_TOOLBAR,
        KENDO_BUTTON,
        KENDO_SVGICON,
        KENDO_POPUP,
        KENDO_DATEINPUTS,
        KENDO_MULTISELECT,
        KENDO_DROPDOWNLIST,
        ToolbarSlotComponent,
        FieldValuePipe,
        FixedPipe,
        LogTimePipe,
        LogDatePipe,
        ZonedDateTimePipe,
        OriginalFlightPipe,
        FlightDetailComponent,
        FlightEditDialogComponent,
    ],
    templateUrl: "./flights2.component.html",
    styleUrl: "./flights2.component.scss",
})
export class Flights2Component {
    private readonly flights = inject(Flights2Service);
    private readonly bootstrap = inject(BootstrapService);
    private readonly translate = inject(TranslateService);
    private readonly notifications = inject(NotificationService);

    readonly today = new Date();
    readonly search = new FormGroup(
        {
            // None for all organization's aircraft (active and in maintenance)
            helicopterIds: new FormControl<number[]>([], { nonNullable: true }),
            from: new FormControl<Date | null>(this.thirtyDaysAgo(), Validators.required),
            to: new FormControl<Date | null>(this.startOfToday(), Validators.required),
        },
        { validators: forwardRange },
    );

    readonly searchError = toSignal(
        this.search.events.pipe(
            startWith(null),
            map(() => (this.search.hasError("range") ? "FLIGHT_LIST.errors.range" : this.search.invalid ? "FLIGHT_LIST.errors.required" : null)),
        ),
        { initialValue: null },
    );

    readonly aircraft = this.bootstrap.aircraft;

    /** The flights arrive in the organization's time zone; the user may read them in UTC or in the browser's. */
    readonly organizationZone = this.bootstrap.organizationTimeZone;
    readonly browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    readonly timeZones = computed(() =>
        [
            { key: "organization", zone: this.organizationZone() },
            { key: "utc", zone: "UTC" },
            ...(this.browserZone !== this.organizationZone() ? [{ key: "browser", zone: this.browserZone }] : []),
        ].map((option) => ({ ...option, label: this.labelOf(option.zone) })),
    );
    /** The kind of time zone the grid shows; the organization's until the user chooses another. */
    readonly timeZoneKey = new FormControl("organization", { nonNullable: true });
    private readonly chosenTimeZone = toSignal(this.timeZoneKey.valueChanges, { initialValue: this.timeZoneKey.value });
    readonly timeZone = computed(() => this.timeZones().find((option) => option.key === this.chosenTimeZone())?.zone ?? this.organizationZone());

    readonly pageSizes = PAGE_SIZES;
    readonly state = signal<State>({ skip: 0, take: PAGE_SIZES[0], sort: [], filter: { logic: "and", filters: [] } });
    readonly page = signal<GridDataResult>({ data: [], total: 0 });

    readonly searchText = signal("");
    // A stream, not instant(): opened directly, the page is built before the translations arrive.
    readonly searchSettings = toSignal(
        this.translate
            .stream("FLIGHT_LIST.searchPlaceholder")
            .pipe(map((placeholder: string): GridSmartBoxSearchSettings => ({ placeholder: placeholder, delay: SEARCH_DELAY_MS, history: false }))),
    );
    /** The flights the CAMO pinned: kept on top of every page, whatever the search, until unpinned. */
    readonly pinned = signal<Flight[]>([]);
    readonly pinnedIds = computed(() => new Set(this.pinned().map((flight) => flight.id)));
    /** The pinned flights first, then the page without them; the total stays the search's. */
    readonly rows = computed<GridDataResult>(() => {
        const page = this.page();
        const pinnedIds = this.pinnedIds();
        const organizationZone = this.organizationZone();
        const zone = this.timeZone();
        const data = [...this.pinned(), ...page.data.filter((flight) => !pinnedIds.has(flight.id))];
        return { data: data.map((flight) => inTimeZone(flight, organizationZone, zone)), total: page.total };
    });
    readonly isPinned = (row: RowArgs): boolean => this.pinnedIds().has(row.dataItem.id);
    readonly loading = signal(false);
    readonly failed = signal(false);

    /** The second engine's columns show when an aircraft of the page has one. */
    readonly twinEngine = computed(() => this.page().data.some((flight) => this.bootstrap.aircraftById().get(flight.helicopterId ?? 0)?.hasEngine2));

    /** The second engine's columns, readings and totals, only when a flight of the page has them. */
    readonly groups = computed(() => (this.twinEngine() ? COLUMN_GROUPS : withoutSecondEngine(COLUMN_GROUPS)));

    /** The pin column is held at the left, after the toggle of the detail. */
    readonly pinColumn = { width: PIN_COLUMN_WIDTH, left: `${EXPAND_COLUMN_WIDTH}px` };

    readonly icons = { yes: checkIcon, pin: pinIcon, unpin: unpinIcon, excel: fileExcelIcon, clear: filterClearIcon, edit: pencilIcon };
    /** The flight the edit dialog is open on. */
    readonly editing = signal<Flight | null>(null);
    /** Changed to recreate the search box empty. */
    readonly searchBoxKey = signal(0);

    /** The file name the export takes from the search: `flights_2026-10-01_2026-10-27.xlsx`. */
    readonly excelFileName = toSignal(
        this.search.valueChanges.pipe(
            startWith(null),
            map(() => {
                const search = this.searchOf();
                return search ? `flights_${this.dayOf(search.from)}_${this.dayOf(search.to)}.xlsx` : "flights.xlsx";
            }),
        ),
        { initialValue: "flights.xlsx" },
    );

    readonly exportData = (): Observable<ExcelExportData> => {
        const search = this.searchOf();
        if (!search) return of({ data: [] });
        const columns = this.groups().flatMap(columnsOf);
        return this.flights.export(search, this.request()).pipe(
            tap((page) => {
                if (page.total > page.data.length) this.toast("FLIGHT_LIST.exportTruncated", { exported: page.data.length, total: page.total });
            }),
            map((page) => ({ data: page.data.map((flight) => this.exportRowOf(inTimeZone(flight, this.organizationZone(), this.timeZone()), columns)) })),
        );
    };

    readonly hint = signal<{ anchor: HTMLElement; hook?: Hook; hoist?: Hoist; roleName?: string; airport?: AirportHint } | null>(null);

    private readonly reload = new Subject<void>();

    constructor() {
        const searchChanges = this.search.valueChanges.pipe(
            debounceTime(SEARCH_DELAY_MS),
            tap(() => this.state.update((state) => ({ ...state, skip: 0 }))),
        );
        merge(searchChanges, this.reload)
            .pipe(
                map(() => this.searchOf()),
                filter((search) => search !== null),
                tap(() => {
                    this.loading.set(true);
                    this.failed.set(false);
                }),
                // A newer search cancels the request of an older one, so a slow answer never overwrites a newer page.
                switchMap((search) => this.flights.search(search, this.request()).pipe(catchError(() => of(null)))),
                takeUntilDestroyed(),
            )
            .subscribe((page) => {
                this.loading.set(false);
                this.failed.set(page === null);
                this.page.set(page ? { data: page.data, total: page.total } : { data: [], total: 0 });
            });
        // The default search, all the aircraft in the current month, needs no choice to run.
        this.load();
    }

    onSearch(event: GridSmartBoxSearchEvent): void {
        event.preventDefault();
        this.searchText.set(event.searchValue.trim());
        this.state.update((state) => ({ ...state, skip: 0 }));
        this.load();
    }

    clearFilters(): void {
        this.searchText.set("");
        this.searchBoxKey.update((key) => key + 1);
        this.state.update((state) => ({ ...state, skip: 0, filter: { logic: "and", filters: [] } }));
        this.search.setValue({ helicopterIds: [], from: this.thirtyDaysAgo(), to: this.startOfToday() });
    }

    /** `Europe/Rome (+01:00)`: the zone and how far it is from UTC now; UTC itself reads `UTC+00:00`. */
    private labelOf(zone: string): string {
        const minutes = offsetFromUtc(zone, new Date());
        const offset = minutes === null ? "" : offsetText(minutes).replace(/^UTC$/, "UTC+00:00");
        if (zone === "UTC") return offset || zone;
        return offset ? `${zone} (${offset.replace(/^UTC/, "")})` : zone;
    }

    togglePin(shown: Flight): void {
        // The row may show the flight in another time zone; the pin keeps it as the organization has it.
        const flight = originalOf(shown);
        this.pinned.update((pinned) => (pinned.some((other) => other.id === flight.id) ? pinned.filter((other) => other.id !== flight.id) : [...pinned, flight]));
    }

    onEdited(): void {
        this.editing.set(null);
        this.toast("FLIGHT_LIST.edit.saved", {}, "success");
        // A change may move the totals of the later flights too, so the whole page comes again.
        this.load();
    }

    showHook(target: EventTarget, hook: Hook): void {
        this.hint.set({ anchor: target as HTMLElement, hook: hook });
    }

    showHoist(target: EventTarget, hoist: Hoist): void {
        this.hint.set({ anchor: target as HTMLElement, hoist: hoist });
    }

    showAirport(target: EventTarget, flight: Flight, field: "departureId" | "arrivalId"): void {
        const airport = this.bootstrap.airportsById().get(flight[field]);
        if (!airport) return;
        const day = (field === "departureId" ? flight.flightDate : flight.arrivalDate) ?? flight.flightDate;
        const offset = airport.timezone && day ? offsetFromBrowser(airport.timezone, new Date(`${day}T12:00:00Z`)) : null;
        this.hint.set({
            anchor: target as HTMLElement,
            airport: {
                code: airport.code,
                name: airport.name,
                city: airport.city,
                country: this.bootstrap.countryOf(airport.countryCode),
                countryCode: airport.countryCode,
                timezone: airport.timezone,
                offset: offset,
                offsetText: offset === null ? "" : this.durationText(Math.abs(offset)),
            },
        });
    }

    /** A role the bootstrap does not know shows no hint. */
    showRole(target: EventTarget, abbreviation: unknown): void {
        const roleName = this.bootstrap.pilotRoleNames().get(this.bootstrap.abbreviationKey(abbreviation));
        if (roleName) this.hint.set({ anchor: target as HTMLElement, roleName: roleName });
    }

    /** `1h 30m`, `2h`, `45m`. */
    private durationText(minutes: number): string {
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        return [hours ? `${hours}h` : "", rest ? `${rest}m` : ""].filter((part) => !!part).join(" ");
    }

    load(): void {
        this.reload.next();
    }

    /** A new sort or filter starts again from the first page: the rows that come back are not the ones that left. */
    dataStateChange(state: DataStateChangeEvent): void {
        const previous = this.state();
        const reordered = JSON.stringify(previous.sort) !== JSON.stringify(state.sort) || JSON.stringify(previous.filter) !== JSON.stringify(state.filter);
        this.state.set(reordered ? { ...state, skip: 0 } : state);
        this.load();
    }

    /** The grid's state with the search box ORed over the searched fields, inside the filter row's conditions. */
    private request(): State {
        const state = this.state();
        const text = this.searchText();
        if (!text) return state;
        const conditions: FilterDescriptor[] = SEARCHED_FIELDS.map((field) => ({ field: field, operator: "contains", value: text }));
        if (/^\d+$/.test(text)) conditions.push({ field: "flightNumber", operator: "eq", value: Number(text) });
        const search: CompositeFilterDescriptor = { logic: "or", filters: conditions };
        return { ...state, filter: { logic: "and", filters: [...(state.filter?.filters ?? []), search] } };
    }

    /** A flight as the export writes it: one value per column, at the column's field, as the cell shows it. */
    private exportRowOf(flight: Flight, columns: FlightListColumn[]): Record<string, unknown> {
        const row: Record<string, unknown> = {};
        for (const column of columns) {
            const value = column.type === "hook" || column.type === "hoist" ? null : fieldValueOf(flight, column.field);
            let cell: unknown;
            switch (column.type) {
                case "hook":
                    cell = flight.hooks.map((hook) => (column.hookField === "hours" ? logTimeOf(hook.hours) : (hook[column.hookField] ?? ""))).join(" / ");
                    break;
                case "hoist":
                    cell = flight.hoists.map((hoist) => (column.hoistField === "hours" ? logTimeOf(hoist.hours) : (hoist[column.hoistField] ?? ""))).join(" / ");
                    break;
                case "boolean":
                    cell = this.translate.instant(value ? "FLIGHT_LIST.yes" : "FLIGHT_LIST.no");
                    break;
                case "decimal":
                    cell = value === null || value === undefined ? null : Number(Number(value).toFixed(2));
                    break;
                case "time":
                    cell = logTimeOf(value);
                    break;
                case "date":
                    cell = logDateOf(value);
                    break;
                case "dateTime":
                    cell = zonedDateTimeOf(value, this.timeZone());
                    break;
                default:
                    cell = value;
            }
            this.setPath(row, column.field, cell);
        }
        return row;
    }

    /** Writes `value` at a dotted path, as the export reads the column's field. */
    private setPath(row: Record<string, unknown>, path: string, value: unknown): void {
        const keys = path.split(".");
        let target = row;
        for (const key of keys.slice(0, -1)) target = (target[key] ??= {}) as Record<string, unknown>;
        target[keys[keys.length - 1]] = value;
    }

    private toast(key: string, params: Record<string, unknown>, style: "warning" | "success" = "warning"): void {
        this.notifications.show({
            content: this.translate.instant(key, params),
            type: { style: style, icon: true },
            animation: { type: "fade", duration: 300 },
            position: { horizontal: "center", vertical: "bottom" },
            hideAfter: 6000,
        });
    }

    private thirtyDaysAgo(): Date {
        return new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() - 30);
    }

    private startOfToday(): Date {
        return new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
    }

    private dayOf(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }

    private searchOf(): FlightSearch | null {
        if (this.search.invalid) return null;
        const { helicopterIds, from, to } = this.search.getRawValue();
        return { helicopterIds: helicopterIds, from: from, to: to };
    }
}
